import {
  ApiError,
  apiGetOrNull,
  apiRequest,
  apiRequestWithEtag,
  ProblemType,
  type IEtagged
} from '@/domains/api';
import type { IPage } from './oversight-contracts';
import type {
  IRefreshRunDetail,
  IRefreshRunRow,
  IRefreshSchedule,
  IRefreshScheduleBody,
  RefreshRunState,
  RefreshTrigger
} from './brokercheck-refresh-contracts';

/**
 * survey-api's scheduled BrokerCheck refresh (SR-222, its block 13; F10 of the advisor page):
 * `/api/v1/admin/brokercheck-refresh/*` — the schedule's read and write, the runs, a run, a start
 * and a cancellation. The sole reader of all six. Every route is `compliance.configure`, admin
 * alone, which is why the screen is Setup's. The queue's `refreshRunId` filter stays
 * `work-items-api.ts`': it is the queue's route, answering the queue's rows.
 *
 * **The schedule's revision is the server's, quoted back as read** — the work items' rule. The read
 * carries it as the `ETag`, which `saveSchedule` sends as `If-Match` (428 without it, 409
 * `revision-conflict` on a stale one) and whose answer carries the next one; it is never parsed
 * into `revision`. A start and a cancellation take none: survey-api's one-active-run index and the
 * run's state are what refuse them.
 *
 * **A run id is a GUID, and anything else is refused before a request** (F10's R10,
 * `registrationRunStorage`'s rule): it comes from the address bar and is spliced into a path, and
 * `runs/.` would reach the list. The route's own `{runId:guid}` would answer 404 for most of what
 * this refuses; refusing here also covers the dot segments the URL parser removes first.
 *
 * Nothing here computes a slot, sums a count, or decides whether a run may start or be cancelled.
 */

const BASE = '/api/v1/admin/brokercheck-refresh';

/** survey-api's run ids, as it issues them — `{runId:guid}` on both routes that take one. */
const RUN_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a value can name a run — what the page asks before it reads one (R10). */
export function isRefreshRunId(value: string): boolean {
  return RUN_ID.test(value);
}

export const brokerCheckRefreshApi = {
  /** The schedule and its `ETag`. A read without one turns Save off on the page (R4). */
  schedule(signal?: AbortSignal): Promise<IEtagged<IRefreshSchedule>> {
    return apiRequestWithEtag<IRefreshSchedule>(`${BASE}/schedule`, { signal });
  },

  /**
   * The whole row under `If-Match`; the answer is the schedule as saved, with its new `ETag` and
   * the next slot it gives. The refusals come in survey-api's order: 428 for no header, 400
   * `invalid-body` with every problem at once (and the binder's at `$.<member>`), then 409
   * `revision-conflict` with `currentRevision`.
   */
  saveSchedule(body: IRefreshScheduleBody, etag: string): Promise<IEtagged<IRefreshSchedule>> {
    return apiRequestWithEtag<IRefreshSchedule>(`${BASE}/schedule`, {
      method: 'PUT',
      body,
      headers: { 'If-Match': etag }
    });
  },

  /** One page of the runs, newest first by when each was created — survey-api's order; `state`
   *  and `trigger` narrow it to one of each, `pageSize` ≤ 100. */
  runs(
    query: { page: number; pageSize?: number; state?: RefreshRunState; trigger?: RefreshTrigger },
    signal?: AbortSignal
  ): Promise<IPage<IRefreshRunRow>> {
    const search = new URLSearchParams({ page: String(query.page) });
    if (query.pageSize !== undefined) search.set('pageSize', String(query.pageSize));
    if (query.state) search.set('state', query.state);
    if (query.trigger) search.set('trigger', query.trigger);
    return apiRequest<IPage<IRefreshRunRow>>(`${BASE}/runs?${search}`, { signal });
  },

  /** A run's record, or `null` for no such run — a bodiless 404, or an id that is not a GUID,
   *  answered here unsent. */
  run(runId: string, signal?: AbortSignal): Promise<Nullable<IRefreshRunDetail>> {
    if (!isRefreshRunId(runId)) return Promise.resolve(null);
    return apiGetOrNull<IRefreshRunDetail>(`${BASE}/runs/${runId}`, { signal });
  },

  /** Run now: 202 with the run, created with its scope; the worker does the work. 409
   *  `run-in-progress` (with `runId`) while any run is active, `anonymized-target` on an
   *  anonymized database. */
  startRun(): Promise<IRefreshRunRow> {
    return apiRequest<IRefreshRunRow>(`${BASE}/runs`, { method: 'POST' });
  },

  /** Cancel: 200 with the run as cancelled; a bodiless 404; 409 `invalid-transition` with
   *  `reason` the state a finished run is in. Ids are the server's, so a non-GUID is a bug here,
   *  thrown rather than sent to another route. */
  cancelRun(runId: string): Promise<IRefreshRunRow> {
    if (!isRefreshRunId(runId)) throw new Error(`"${runId}" is not a refresh run's id.`);
    return apiRequest<IRefreshRunRow>(`${BASE}/runs/${runId}/cancellation`, { method: 'POST' });
  }
};

/**
 * What a refusal of the refresh's writes says, told apart by status and `type` alone — never by
 * `title` (F10's R4, R8). `null` for anything else: a 400 (`problems[]`, read by the form at its
 * paths), a 404, or an answer that never arrived, each of which its caller handles in its own way.
 */
export type RefreshRefusal =
  /** 409 `revision-conflict` — another admin saved the schedule; `currentRevision` where sent. */
  | { kind: 'revision-conflict'; currentRevision: Nullable<number> }
  /** 428 — this page sent a save without `If-Match`: its fault, not the admin's. */
  | { kind: 'precondition-required' }
  /** 409 `run-in-progress` — the active run, where its id came with the refusal. */
  | { kind: 'run-in-progress'; runId: Nullable<string> }
  /** 409 `anonymized-target` — said in survey-api's words. */
  | { kind: 'anonymized-target'; title: Nullable<string> }
  /** 409 `invalid-transition` — a cancellation of a finished run, `state` the one it is in. */
  | { kind: 'invalid-transition'; state: Nullable<RefreshRunState> };

export function refreshRefusal(error: unknown): Nullable<RefreshRefusal> {
  if (!(error instanceof ApiError)) return null;
  const problem = error.problem ?? {};
  const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);
  if (error.status === 428) return { kind: 'precondition-required' };
  if (error.status !== 409) return null;
  if (error.isProblem(ProblemType.revisionConflict)) {
    const current = problem.currentRevision;
    return {
      kind: 'revision-conflict',
      currentRevision: typeof current === 'number' ? current : null
    };
  }
  if (error.isProblem(ProblemType.runInProgress)) {
    return { kind: 'run-in-progress', runId: text(problem.runId) };
  }
  if (error.isProblem(ProblemType.anonymizedTarget)) {
    return { kind: 'anonymized-target', title: text(problem.title) };
  }
  if (error.isProblem(ProblemType.invalidTransition)) {
    return { kind: 'invalid-transition', state: text(problem.reason) };
  }
  return null;
}
